import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';

interface ReviewUnavailableCardProps {
  /**
   * Which failure occurred. `load-failed` means the record couldn't be fetched;
   * `not-generated` means it was fetched but holds no reviewable document —
   * usually a draft abandoned before the generate step finished.
   */
  reason: 'load-failed' | 'not-generated';
  onBack: () => void;
  /** Offered only when generating is actually the fix. */
  onGoToGenerate?: () => void;
}

/** Terminal state for the review route: there is nothing to show. */
export function ReviewUnavailableCard({
  reason,
  onBack,
  onGoToGenerate,
}: ReviewUnavailableCardProps) {
  const { t } = useTranslation();

  const description =
    reason === 'load-failed'
      ? t('TEMPLATES_REVIEWPAGE_FAILEDTOLOADPDF')
      : t('TEMPLATES_REVIEWPAGE_NOTGENERATED', {
          defaultValue:
            "This document hasn't been generated yet, so there's nothing to review. Go back and finish the generate step.",
        });

  return (
    <div className="container max-w-4xl mx-auto py-16">
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">
            {t('TEMPLATES_REVIEWPAGE_NOPDFAVAILABLE')}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button variant="primaryOutline" onClick={onBack}>
            {t('TEMPLATES_REVIEWPAGE_BACK', { defaultValue: 'Back' })}
          </Button>
          {onGoToGenerate && (
            <Button variant="primary" onClick={onGoToGenerate}>
              {t('TEMPLATES_REVIEWPAGE_GOTOGENERATE', { defaultValue: 'Go to generate' })}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
